import { Component, Input } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import {
  ColumnDefinitionV3,
  ColumnRendererComponentV3,
  PageContentLayoutComponent,
  PageHeaderModule,
  SearchBarV2Module,
  TableServiceV3,
  TableV3Component,
} from '@gea/digital-ui-lib';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import {
  ActiveChipFilters,
  Chip,
  HrtFilterChipsComponent,
} from 'src/app/shared/components/hrt-filter-chips/hrt-filter-chips.component';
import { Asset } from 'src/app/shared/data/data.generated';
import { ApiService } from 'src/app/shared/services/api.service';
import { Filter, Table } from 'src/app/shared/services/api/table';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { WebsocketService } from 'src/app/shared/services/websocket.service';
import { AssetIconRenderer } from '../assets/asset-renderers/asset-icon-renderer.component';
import { AssetTypeRenderer } from '../assets/asset-renderers/asset-type-renderer.component';

@Component({
  selector: 'gea-asset-table',
  templateUrl: './asset-table.component.html',
  styleUrls: ['./asset-table.component.scss'],
  standalone: true,
  imports: [
    PageHeaderModule,
    SearchBarV2Module,
    HrtFilterChipsComponent,
    TableV3Component,
    PageContentLayoutComponent,
    MatIconModule,
  ],
})
export class AssetTableComponent {
  @Input() assetTable: Table<Asset> = new Table<Asset>(this.loader);
  @Input() showMotorStatus?: boolean = false;
  deviceData: any;
  annunStatuses: any = {};
  socketInterval: any;

  public columnDefinitions: ColumnDefinitionV3[] = [
    {
      displayName: '',
      key: 'Icon',
      sortable: false,
      minWidth: 200,
      renderer: {
        component: AssetIconRenderer as ColumnRendererComponentV3<any>,
      },
    },
    {
      displayName: 'Asset Type',
      key: 'AssetTypeId',
      sortable: true,
      minWidth: 200,
      renderer: {
        component: AssetTypeRenderer as ColumnRendererComponentV3<any>,
      },
    },
    {
      displayName: 'Reference Name',
      key: 'GeaReferenceName',
      sortable: true,
      minWidth: 200,
    },
    {
      displayName: 'Manufacturer',
      key: 'Manufacturer',
      sortable: true,
      minWidth: 200,
    },
    {
      displayName: 'Model',
      key: 'Model',
      sortable: true,
      minWidth: 200,
    },
    {
      displayName: 'Alt Model',
      key: 'AlternativeModel',
      sortable: false,
      minWidth: 300,
    },
    {
      displayName: 'Serial Number',
      key: 'SerialNumber',
      sortable: true,
      minWidth: 200,
    },
    {
      displayName: 'Project Number',
      key: 'SalesOrderNumber',
      sortable: true,
      minWidth: 200,
    },
    // {
    //   displayName: 'Anunciation Status',
    //   key: 'Annunciation',
    //   sortable: true,
    //   minWidth: 200,
    // },
  ];

  // public extraColumns: ColumnDefinitionV3[] = [
  //   {
  //     displayName: 'Motor Status',
  //     key: 'MotorStatus',
  //     sortable: true,
  //     minWidth: 200,
  //   },
  // ];

  // public shutdownStatusMapping: any = {
  //   0: 'Normal',
  //   1: 'Warning',
  //   2: 'Shutdown',
  // };
  isSocketOpen: boolean = false;
  motorStatusText: string;

  // new table values
  tableId: string = 'hrtc-assets-table';
  searchPlaceholder = 'Search Assets';
  destroy$ = new Subject<void>();
  searchInput = new Subject<string>();
  count: number = 0;
  sortKey?: string;
  sortOrder?: string;
  statusFilters: string[] = [];
  page: number = 0;
  pageSize: number = 10;
  assetSearch?: string;
  assetsResponse: Asset[] = [];
  isTableLoading: boolean = true;
  filters: Filter = {};
  addUserButtonDisabled: boolean = true;
  activeChipFilters: ActiveChipFilters = { searchText: '', filters: {} };
  searchForm = this.fb.group({
    search: [''],
  });

  constructor(
    private router: Router,
    private websocketService: WebsocketService,
    private api: ApiService,
    private loader: LoaderService,
    private tableService: TableServiceV3,
    private fb: FormBuilder,
    private utilityService: UtilityService,
  ) {}

  ngOnInit() {
    // this.showMotorStatus
    //   ? this.columnDefinitions.push(...this.extraColumns)
    //   : null;
    // this.pingWebSocket();
    this.handleHeaderClick();
    this.handleSearchChanged();
  }

  private handleHeaderClick() {
    this.tableService
      .getFilterTableSettings(this.tableId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        if (!settings?.columns) return;
        if (settings?.columns) {
          this.filters = this.buildBackendFiltersFromColumns(
            settings.columns,
            this.columnDefinitions,
          );
        }
        if (settings?.page !== undefined) {
          this.page = settings.page;
        }
        this.pageSize = settings?.pageSize ?? this.pageSize;
        const sortedColumn = Object.keys(settings.columns).find(
          (key) => settings.columns[key].sort ?? '',
        );

        if (sortedColumn) {
          this.sortKey = sortedColumn;
          if (settings.columns[sortedColumn].sort) {
            this.sortOrder =
              settings.columns[sortedColumn].sort === 'desc' ? 'True' : 'False';
          } else {
            this.sortOrder = '';
          }
        }

        this.activeChipFilters.filters = settings?.columns
          ? settings.columns
          : this.activeChipFilters.filters;

        this.fetchAssetList();
      });
  }

  buildBackendFiltersFromColumns(columns: any, columnDefinitions: any[]): any {
    const backendFilters: any = {};

    columnDefinitions.forEach((colDef) => {
      const key = colDef.key;
      const col = columns[key];
      if (
        col &&
        col.filter &&
        Array.isArray(col.filter) &&
        col.filter.length > 0
      ) {
        // Find options for this column if available
        const options = colDef.filter?.config?.options || [];
        const values = col.filter.map((val: any) => {
          // Try to find a matching option for key/label mapping
          const found = options.find(
            (opt: any) =>
              opt.value?.toString() === val?.toString() ||
              opt.key?.toString() === val?.toString() ||
              opt.label === val ||
              opt.name === val,
          );
          return found
            ? {
                key: found.key ?? found.value ?? val,
                label: found.label ?? found.name ?? val,
              }
            : { key: val, label: val };
        });

        backendFilters[key] = {
          isClientSide: false,
          type: 'multiSelect',
          values,
        };
      }
    });

    return backendFilters;
  }

  private handleSearchChanged(): void {
    this.searchInput.pipe(debounceTime(500)).subscribe((searchTerm: string) => {
      if (searchTerm.length >= 3 || searchTerm.length == 0) {
        this.assetSearch = searchTerm;
        this.utilityService.setTableSearchSetting(
          this.tableId,
          this.assetSearch,
        );
        this.page = 0; // Reset to first page on new table search
        this.fetchAssetList();
      }
    });
  }

  searchValueChanged(newValue: string): void {
    this.searchInput.next(newValue);
  }

  fetchAssetList(): void {
    this.isTableLoading = true;
    if (this.utilityService.getTableSearchSetting(this.tableId)) {
      this.assetSearch = this.utilityService.getTableSearchSetting(
        this.tableId,
      );
    }
    const listOptions = {
      Search: this.assetSearch ?? '',
      SortColumn: this.sortKey ?? '',
      SortDesc: this.sortOrder ?? '',
      PageSize: this.pageSize,
      Page: this.page,
      HasPaging: true,
      Filters: this.filters || {},
    };
    this.api.Assets.GetAssets(listOptions).subscribe({
      next: (response: AssetListResponse) => {
        this.assetsResponse = response.Data ?? [];
        this.activeChipFilters.searchText = this.assetSearch ?? '';
        this.count = response.Count;
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {
        this.isTableLoading = false;
      },
    });
  }

  // pingWebSocket() {
  //   const bucketList: string = this.websocketService.connectedBuckets.join(',');
  //   const messageStr = JSON.stringify({
  //     deviceList: bucketList,
  //   });
  //   this.socketInterval = setInterval(() => {
  //     this.websocketService.send(messageStr);
  //     this.isSocketOpen = true;
  //   }, 5000);
  //   this.websocketService.liveData.subscribe((dataReceived) => {
  //     if (dataReceived && Object.keys(dataReceived).length > 0) {
  //       this.deviceData = dataReceived;
  //       this.extractAnnunStatuses();
  //     }
  //   });
  // }

  // extractAnnunStatuses() {
  //   Object.keys(this.deviceData).forEach((key) => {
  //     const data = this.deviceData[key];
  //     const obj = {
  //       annun_status: data['compressor_Annunciation_Status'],
  //       motor_status: data['motor_Running'],
  //     };
  //     this.annunStatuses[key] = obj;
  //   });
  // }

  // getAnnunStatusValue(connectionId: string) {
  //   if (connectionId) {
  //     return this.annunStatuses[connectionId]
  //       ? this.annunStatuses[connectionId].annun_status
  //       : null;
  //   } else {
  //     return null;
  //   }
  // }

  // getAnnunImage(annun_status: number) {
  //   if (annun_status != null) {
  //     switch (annun_status) {
  //       case 0:
  //         return '../../../assets/icons/Check_Circle.svg';
  //       case 1:
  //         return '../../../assets/icons/Warning.svg';
  //       case 2:
  //         return '../../../assets/icons/Error.svg';
  //       default:
  //         return null;
  //     }
  //   } else {
  //     return '../../../assets/icons/NotAvailable.png';
  //   }
  // }

  // getAnnunText(annun_status: number) {
  //   if (annun_status != null) {
  //     return this.shutdownStatusMapping[annun_status];
  //   } else {
  //     return 'No Data';
  //   }
  // }

  getMotorStatus(key: string) {
    if (!this.deviceData) return;
    if (this.websocketService.connectedAssets.includes(key)) {
      if (this.annunStatuses[key]) {
        if (this.annunStatuses[key]?.motor_status) {
          this.motorStatusText = 'Running';
          return '../assets/icons/Check_Circle.svg';
        } else if (!this.annunStatuses[key]?.motor_status) {
          this.motorStatusText = 'Stopped';
          return '../assets/product-icons/Error.svg';
        }
      } else {
        this.motorStatusText = 'No Data';
        return '../assets/icons/NotAvailable.png';
      }
    }
    return null;
  }

  openAsset(id: number) {
    this.router.navigate(['/asset-details', { id: id }]);
  }

  // openRealTimeForAsset(id: number, externalId: string) {
  //   if (this.getAnnunStatusValue(externalId) != null) {
  //     this.router.navigate(['/asset-details', { id: id, realTime: true }]);
  //   } else {
  //     this.openAsset(id);
  //   }
  // }

  searchChange(searchText: string) {
    if (
      searchText.trim() !== this.assetTable.inputSubject.getValue() ||
      searchText === ''
    ) {
      this.assetTable.inputSubject.next(searchText);
    }
  }

  onRowClicked(asset: Asset) {
    this.router.navigate(['/asset-details', { isNew: false, id: asset.Id }]);
  }

  onChipRemoved(chip: Chip) {
    if (chip && chip.chipKey === 'clearAll') {
      // Clear all filters
      this.activeChipFilters = { searchText: '', filters: {} };
      this.assetSearch = '';
      this.sortKey = '';
      this.sortOrder = '';
      this.filters = {};
      this.utilityService.setTableSearchSetting(this.tableId, '');
      this.searchForm.get('search')?.setValue('');
      this.tableService.setFilterTableSettings(this.tableId, {
        columns: this.activeChipFilters.filters,
        page: this.page,
        pageSize: this.pageSize,
      });
    } else if (
      chip &&
      chip.chipKey === 'searchText' &&
      chip.chipValue !== '' &&
      this.activeChipFilters.searchText === chip.chipValue
    ) {
      // Clear user search
      this.assetSearch = '';
      this.utilityService.setTableSearchSetting(this.tableId, '');
      this.activeChipFilters.searchText = '';
      this.tableService.setFilterTableSettings(this.tableId, {
        columns: this.activeChipFilters.filters,
        page: this.page,
        pageSize: this.pageSize,
      });
      this.searchForm.get('search')?.setValue('');
    } else {
      if (chip && chip.chipKey !== 'searchText' && chip.chipValue !== '') {
        // 1. If chipValue is 'asc' or 'desc', remove "sort chip" and update documents
        if (chip.chipValue === 'asc' || chip.chipValue === 'desc') {
          if (this.activeChipFilters.filters[chip.chipKey]) {
            this.activeChipFilters.filters[chip.chipKey].sort = '';
            this.sortKey = '';
            this.sortOrder = '';
            this.tableService.setFilterTableSettings(this.tableId, {
              columns: this.activeChipFilters.filters,
              page: this.page,
              pageSize: this.pageSize,
            });
            this.fetchAssetList();
          }
        } else {
          // 2. Remove "filter chip" for chipKey with chipValue
          if (
            this.activeChipFilters.filters[chip.chipKey] &&
            Array.isArray(this.activeChipFilters.filters[chip.chipKey].filter)
          ) {
            this.activeChipFilters.filters[chip.chipKey].filter =
              this.activeChipFilters.filters[chip.chipKey].filter.filter(
                (val: any) => val !== chip.chipValue,
              );
            // Rebuild backend filters and update documents
            this.filters = this.buildBackendFiltersFromColumns(
              this.activeChipFilters.filters,
              this.columnDefinitions,
            );
            this.tableService.setFilterTableSettings(this.tableId, {
              columns: this.activeChipFilters.filters,
              page: this.page,
              pageSize: this.pageSize,
            });
            this.fetchAssetList();
          }
        }
      }
    }
  }

  exportTable() {
    const listOptions = {
      Search: this.assetSearch ?? '',
      SortColumn: this.sortKey ?? '',
      SortDesc: this.sortOrder ?? '',
      PageSize: this.pageSize,
      Page: this.page,
      HasPaging: true,
      Filters: this.filters || {},
    };
    // const listOptions = this.siteTable.View;
    listOptions.Page = 0;
    listOptions.HasPaging = false;
    this.api.Assets.ExportAssets(
      listOptions,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      'Asset',
    );
  }

  ngOnDestroy() {
    clearInterval(this.socketInterval);
  }
}

export interface AssetListResponse {
  Count: number;
  Data: Asset[];
}
